/**
 * Geofence GET + PUT hooks (DISPATCH-04 / GEO-02).
 *
 * Phase 9 / Plan 09-06 — wraps the Plan 09-02 endpoints:
 *   GET  /v1/sites/:siteId/geofence  → { geofence: SiteGeofenceData | null }
 *   PUT  /v1/sites/:siteId/geofence  → { ok: true; geofence: SiteGeofenceData }
 *
 * The PUT requires EDIT_GEOFENCE (DISPATCH_MANAGER+). A DISPATCHER will receive
 * a 403 — the editor surfaces the UI-SPEC error copy rather than hiding the form.
 *
 * Spec: 09-06-PLAN.md Task 1; 09-PATTERNS.md geofence-put / geofence-get sections.
 */

import { useQueryClient, type UseQueryResult, type UseMutationResult } from '@tanstack/react-query';
import type { SiteGeofenceData } from '@lodex-pro/shared';
import { useApiQuery, useApiMutation, useApiOpts } from '@lodex-pro/web-shared';
import { SITES_QUERY_KEY } from './use-sites.js';

// ---------------------------------------------------------------------------
// Query key
// ---------------------------------------------------------------------------

export type GeofenceSiteType = 'PICKUP' | 'DISPOSAL' | 'TANK_BATTERY';

export const GEOFENCE_QUERY_KEY = (
  siteId: string,
  siteType: GeofenceSiteType = 'PICKUP',
): readonly [string, string, string] => ['geofence', siteType, siteId] as const;

// ---------------------------------------------------------------------------
// GET hook
// ---------------------------------------------------------------------------

/**
 * Fetch the active geofence for a site. `siteType` selects PICKUP (company_sites,
 * default) or DISPOSAL (disposal_facilities). Disabled when siteId is null.
 */
export function useGeofence(
  siteId: string | null,
  siteType: GeofenceSiteType = 'PICKUP',
): UseQueryResult<{ geofence: SiteGeofenceData | null }> {
  const opts = useApiOpts();
  return useApiQuery<{ geofence: SiteGeofenceData | null }>(
    opts,
    `/v1/sites/${siteId ?? ''}/geofence?siteType=${siteType}`,
    {
      queryKey: GEOFENCE_QUERY_KEY(siteId ?? '', siteType),
      enabled: siteId != null,
    },
  );
}

// ---------------------------------------------------------------------------
// PUT hook
// ---------------------------------------------------------------------------

export interface SaveGeofenceBody {
  // Phase 8 / Task 8.1 (disposal) — PICKUP (company_sites, default) or DISPOSAL.
  siteType?: GeofenceSiteType;
  // Phase 8 / Task 8.2 (DEC-080) — hybrid geometry + verification.
  geofenceType: 'RADIUS' | 'POLYGON';
  /** RADIUS mode — feet (100–2640). */
  radiusFt?: number;
  /** RADIUS mode — pin position; persisted to company_sites center. */
  centerLat?: number;
  centerLng?: number;
  /** POLYGON mode — closed-ring GeoJSON. */
  polygonGeojson?: string;
  /** Verify & Save → true; Save Draft → false/omitted. verified_by is server-set. */
  isVerified?: boolean;
  dwellTimeSeconds?: number;
  accuracyThresholdFt?: number;
}

export interface SaveGeofenceResponse {
  ok: true;
  geofence: SiteGeofenceData;
}

/**
 * Persist the drawn polygon via PUT /v1/sites/:siteId/geofence.
 * On success, invalidates both the geofence query and the sites list
 * (so the sidebar's geofenceExists badge updates immediately).
 */
export function useSaveGeofence(
  siteId: string,
  siteType: GeofenceSiteType = 'PICKUP',
): UseMutationResult<SaveGeofenceResponse, Error, SaveGeofenceBody> {
  const opts = useApiOpts();
  const queryClient = useQueryClient();

  return useApiMutation<SaveGeofenceBody, SaveGeofenceResponse>(
    opts,
    `/v1/sites/${siteId}/geofence`,
    'PUT',
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: GEOFENCE_QUERY_KEY(siteId, siteType) });
        void queryClient.invalidateQueries({ queryKey: SITES_QUERY_KEY });
      },
    },
  );
}
